import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";

/*
 * Remote self-hosted LLM servers (llm.prod.oelement.net).
 *
 * Two OpenAI-compatible endpoints live behind one GPU instance:
 *   - Kimi (Kimi-Linear-48B-A3B-Instruct via llama.cpp)  →  /kimi/v1/...
 *   - DeepSeek (DeepSeek-R1 32B via Ollama)              →  /deepseek/v1/...
 *
 * The instance stops itself when idle. A small wake endpoint (Lambda) reports
 * the instance state and starts it on demand; this context tracks that
 * lifecycle and exposes engines with the SAME `.chat.completions.create()`
 * streaming contract as WebLLM, so the shared ChatPanel can use either.
 */

const BASE_URL = process.env.REACT_APP_LLM_BASE_URL || "https://llm.prod.oelement.net";
const WAKE_URL = process.env.REACT_APP_LLM_WAKE_URL || "https://ttcnf5w66d.execute-api.us-east-2.amazonaws.com/";
const API_KEY = process.env.REACT_APP_LLM_API_KEY || "78d520bc354d4c2cc7cd3c3321fd65715562adaa09e32d16122b9648f9ba5b78";

export const REMOTE_LLM_MODELS = {
  kimi: {
    key: "kimi",
    label: "Kimi",
    detail: "Kimi-Linear 48B (Moonshot AI)",
    path: "/kimi/v1/chat/completions",
    healthPath: "/kimi/health",
    model: "kimi-linear-48b",
  },
  deepseek: {
    key: "deepseek",
    label: "DeepSeek",
    // 14B runs fully on the GPU next to Kimi; the 32B variant spills to CPU
    // on the shared 48 GB card and is unusably slow (2-4 tok/s).
    detail: "DeepSeek-R1 14B",
    path: "/deepseek/v1/chat/completions",
    healthPath: "/deepseek/api/version",
    model: "deepseek-r1:14b",
  },
};

const AUTH_HEADERS = { Authorization: `Bearer ${API_KEY}` };

/* Parse an OpenAI-style SSE stream into an async generator of chunk objects. */
async function* streamChatCompletion(url, body, signal) {
  const res = await fetch(url, {
    method: "POST",
    headers: { ...AUTH_HEADERS, "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(`LLM server error ${res.status}: ${text.slice(0, 200)}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith("data:")) continue;
        const payload = trimmed.slice(5).trim();
        if (payload === "[DONE]") return;
        try {
          yield JSON.parse(payload);
        } catch {
          // Skip malformed keep-alive fragments.
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function makeEngine(modelKey) {
  const cfg = REMOTE_LLM_MODELS[modelKey];
  return {
    chat: {
      completions: {
        create: async (params) => {
          const { stream_options, ...rest } = params;
          return streamChatCompletion(`${BASE_URL}${cfg.path}`, {
            model: cfg.model,
            ...rest,
            ...(stream_options ? { stream_options } : {}),
          });
        },
      },
    },
  };
}

const RemoteLLMContext = createContext(null);

export function RemoteLLMProvider({ children }) {
  // Server lifecycle: unknown → stopped | starting | loading | ready | error
  const [serverState, setServerState] = useState("unknown");
  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
    if (pollRef.current) clearTimeout(pollRef.current);
  }, []);

  const checkModelHealth = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}${REMOTE_LLM_MODELS.kimi.healthPath}`, {
        headers: AUTH_HEADERS,
      });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  const fetchInstanceState = useCallback(async (method) => {
    if (!WAKE_URL) return null;
    try {
      const res = await fetch(WAKE_URL, { method, headers: AUTH_HEADERS });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, []);

  /* Poll until the models answer, while the instance boots + loads weights. */
  const pollUntilReady = useCallback(async () => {
    if (await checkModelHealth()) {
      if (mountedRef.current) setServerState("ready");
      return;
    }
    if (mountedRef.current) {
      pollRef.current = setTimeout(pollUntilReady, 8000);
    }
  }, [checkModelHealth]);

  const refreshStatus = useCallback(async () => {
    if (await checkModelHealth()) {
      setServerState("ready");
      return;
    }
    const info = await fetchInstanceState("GET");
    const state = info?.state;
    if (state === "running" || state === "pending") {
      setServerState("loading");
      pollUntilReady();
    } else if (state === "stopped" || state === "stopping") {
      setServerState("stopped");
    } else {
      setServerState("error");
    }
  }, [checkModelHealth, fetchInstanceState, pollUntilReady]);

  const wake = useCallback(async () => {
    setServerState("starting");
    const info = await fetchInstanceState("POST");
    if (!info) {
      setServerState("error");
      return;
    }
    setServerState("loading");
    pollUntilReady();
  }, [fetchInstanceState, pollUntilReady]);

  const value = useMemo(
    () => ({ serverState, refreshStatus, wake, makeEngine, models: REMOTE_LLM_MODELS }),
    [serverState, refreshStatus, wake]
  );

  return <RemoteLLMContext.Provider value={value}>{children}</RemoteLLMContext.Provider>;
}

export function useRemoteLLM() {
  const ctx = useContext(RemoteLLMContext);
  if (!ctx) throw new Error("useRemoteLLM must be used within RemoteLLMProvider");
  return ctx;
}
